import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy, Download, RefreshCw, CheckCircle2, Loader2, ShieldAlert,
  Terminal, Server, LinkIcon, Building2, Hash, TrendingUp, ArrowRight, Clock,
} from "lucide-react";
import { api, BACKEND_ORIGIN } from "@/lib/api";
import { useAccounts } from "@/context/AccountContext";

// ─── helpers ────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 5000;

const secondsUntil = (isoTs) => {
  if (!isoTs) return 0;
  return Math.max(0, Math.floor((new Date(isoTs).getTime() - Date.now()) / 1000));
};

const fmtCurrency = (v, ccy = "USD") => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, minimumFractionDigits: 2 })
      .format(Number(v));
  } catch { return `${Number(v).toFixed(2)} ${ccy}`; }
};

const fmtMs = (ts) => (ts ? new Date(ts).toLocaleTimeString() : "—");

// ─── small UI atoms ─────────────────────────────────────────────────────
const StepPill = ({ n, label, state }) => {
  // state: 'done' | 'active' | 'pending'
  const bg = state === "done" ? "bg-[#10b981]/15 border-[#10b981] text-[#10b981]"
    : state === "active" ? "bg-[#3b82f6]/15 border-[#3b82f6] text-[#3b82f6]"
    : "bg-[#111827] border-[#1f2937] text-[#6b7280]";
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border font-mono text-xs ${bg}`}
         data-testid={`connect-step-pill-${n}`}>
      <span className="w-5 h-5 rounded-full border-current flex items-center justify-center text-[10px] font-bold">
        {state === "done" ? <CheckCircle2 className="w-3 h-3" /> : n}
      </span>
      <span className="font-bold uppercase tracking-wider">{label}</span>
    </div>
  );
};

const CopyBox = ({ label, value, testId, mono = true, big = false }) => (
  <div>
    {label && <div className="vx-label mb-1.5">{label}</div>}
    <div className="flex items-stretch gap-2">
      <div
        className={`flex-1 vx-input truncate ${mono ? "font-mono" : ""} ${big ? "text-2xl font-bold tracking-[0.4em] text-center py-3" : "text-xs"}`}
        data-testid={`${testId}-value`}
      >
        {value || "—"}
      </div>
      <button
        onClick={() => {
          navigator.clipboard.writeText(value || "");
          toast.success(`${label || "Value"} copied`);
        }}
        data-testid={`${testId}-copy`}
        className="h-auto px-3 rounded-md border border-[#1f2937] hover:bg-[#1f2937] transition-colors flex items-center gap-1.5 text-xs font-mono"
      >
        <Copy className="w-3.5 h-3.5" /> Copy
      </button>
    </div>
  </div>
);

const Kbd = ({ children }) => (
  <span className="inline-block px-1.5 py-0.5 border border-[#1f2937] rounded bg-[#0a0e1a] text-[11px] font-mono text-[#e5e7eb]">
    {children}
  </span>
);

const KV = ({ icon: Icon, label, value, testId }) => (
  <div className="vx-card p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#3b82f6]" />}
      <div className="vx-label">{label}</div>
    </div>
    <div className="font-mono text-sm font-bold truncate">{value || "—"}</div>
  </div>
);

// ─── main page ──────────────────────────────────────────────────────────
export default function Connect() {
  const { refresh: refreshAccounts, setActiveAccountId } = useAccounts();
  const navigate = useNavigate();

  // Local state
  const [accountName, setAccountName] = useState("MT5 · Main");
  const [brokerId, setBrokerId] = useState("generic");
  const [brokers, setBrokers] = useState([]);
  const [provisioning, setProvisioning] = useState(false);
  const [regen, setRegen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Provisioned account state (survives refresh via localStorage)
  const [prov, setProv] = useState(() => {
    try { return JSON.parse(localStorage.getItem("veerox_connect_prov") || "null"); }
    catch { return null; }
  });
  const [status, setStatus] = useState(null);   // /claim-status response
  const [nowTick, setNowTick] = useState(Date.now());
  const pollTimer = useRef(null);

  const backendUrl = BACKEND_ORIGIN;
  const productionUrl = "https://veeroxalgo.com";
  const isPreview = backendUrl !== productionUrl;

  // Persist prov to localStorage so a refresh mid-wizard keeps state.
  useEffect(() => {
    if (prov) localStorage.setItem("veerox_connect_prov", JSON.stringify(prov));
    else localStorage.removeItem("veerox_connect_prov");
  }, [prov]);

  // Load broker list once
  useEffect(() => {
    api.get("/brokers").then(r => setBrokers(r.data?.brokers || []))
      .catch(() => setBrokers([{ broker_id: "generic", display_name: "Generic" }]));
  }, []);

  // ── polling loop (5s) while we have a provisioned account
  const poll = useCallback(async () => {
    if (!prov?.account_id) return;
    try {
      const r = await api.get(`/accounts/${prov.account_id}/claim-status`);
      setStatus(r.data);
    } catch (e) {
      // 404 → account got deleted; clear prov to reset the wizard
      if (e?.response?.status === 404) {
        setProv(null);
        setStatus(null);
        toast.error("Provisional account no longer exists — start over.");
      }
    }
  }, [prov?.account_id]);

  useEffect(() => {
    if (!prov?.account_id) return;
    poll();  // immediate
    pollTimer.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [prov?.account_id, poll]);

  // 1-second local clock to drive the countdown UI (no extra network)
  useEffect(() => {
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  // ── derived: which step is active
  const claimed = !!status?.claimed;
  const codeExpired = !!status?.claim_code_expired;
  const currentStep = !prov ? 1 : (!claimed ? 2 : 3);
  const codeSecondsLeft = useMemo(
    () => secondsUntil(status?.claim_code_expires_at || prov?.expires_at),
    [status?.claim_code_expires_at, prov?.expires_at, nowTick],
  );

  // ── actions
  const provisionAccount = async () => {
    if (!accountName.trim()) return toast.error("Account name required");
    setProvisioning(true);
    try {
      const r = await api.post("/accounts/provision", {
        account_name: accountName.trim(),
        broker_id: brokerId,
      });
      setProv(r.data);
      setStatus(null);
      await refreshAccounts();
      toast.success("Provisional account created — paste the code into your EA.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to provision account";
      toast.error(msg);
    } finally {
      setProvisioning(false);
    }
  };

  const regenerateCode = async () => {
    if (!prov?.account_id) return;
    setRegen(true);
    try {
      const r = await api.post(`/accounts/${prov.account_id}/regenerate-claim-code`);
      setProv({ ...prov, claim_code: r.data.claim_code, expires_at: r.data.expires_at });
      await poll();
      toast.success("New 6-char code generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to regenerate code");
    } finally {
      setRegen(false);
    }
  };

  const downloadEA = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/ea/download", { params: { version: "1.51" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "veerox_ea_v1_51.mq5";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("EA v1.51 downloaded — check your Downloads folder");
    } catch {
      toast.error("Failed to download EA");
    } finally {
      setDownloading(false);
    }
  };

  const startOver = () => {
    if (!window.confirm("Discard this provisional account and start over?")) return;
    setProv(null); setStatus(null);
  };

  const finishAndOpenDashboard = async () => {
    if (status?.account_id) setActiveAccountId(status.account_id);
    await refreshAccounts();
    setProv(null); setStatus(null);
    navigate("/dashboard");
  };

  // ─── render ─────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl space-y-8" data-testid="connect-page">
      {/* Header */}
      <div>
        <div className="vx-label">Connect MT5</div>
        <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">
          Connect your MetaTrader 5 account
        </h1>
        <p className="text-[#9ca3af] mt-2 max-w-2xl">
          One unified flow — auto-detects your broker, server and login from the EA. No
          manual paste. No fake broker dropdowns. Real handshake via <span className="font-mono text-[#e5e7eb]">WebRequest()</span>.
        </p>
      </div>

      {/* Step tracker */}
      <div className="flex items-center gap-3 flex-wrap">
        <StepPill n={1} label="Provision" state={currentStep > 1 ? "done" : currentStep === 1 ? "active" : "pending"} />
        <ArrowRight className="w-4 h-4 text-[#374151]" />
        <StepPill n={2} label="Claim in EA" state={currentStep > 2 ? "done" : currentStep === 2 ? "active" : "pending"} />
        <ArrowRight className="w-4 h-4 text-[#374151]" />
        <StepPill n={3} label="Live" state={currentStep === 3 ? "active" : "pending"} />
      </div>

      {/* ── Step 1: Provision ────────────────────────────────────── */}
      {currentStep === 1 && (
        <div className="vx-card p-6 space-y-5" data-testid="step-1-provision-card">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4 text-[#3b82f6]" />
            <div className="vx-label">Step 1 · Create a provisional account</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="vx-label mb-1.5">Account nickname</div>
              <input
                type="text"
                value={accountName}
                onChange={e => setAccountName(e.target.value)}
                placeholder="e.g. MT5 · Vantage Live"
                className="vx-input w-full"
                data-testid="account-name-input"
                maxLength={60}
              />
            </div>
            <div>
              <div className="vx-label mb-1.5">Broker profile (symbol suffix)</div>
              <select
                value={brokerId}
                onChange={e => setBrokerId(e.target.value)}
                className="vx-input w-full"
                data-testid="broker-select"
              >
                {brokers.map(b => (
                  <option key={b.broker_id} value={b.broker_id}>{b.display_name}</option>
                ))}
              </select>
              <div className="text-[11px] text-[#6b7280] mt-1.5 leading-relaxed">
                This is only for symbol suffix normalization (e.g. <span className="font-mono">EURUSD.pro</span>).
                The broker <b>company + server</b> shown on your dashboard will be auto-detected from the EA.
              </div>
            </div>
          </div>
          <button
            onClick={provisionAccount}
            disabled={provisioning}
            data-testid="provision-account-btn"
            className="h-11 px-6 rounded-md bg-[#3b82f6] hover:bg-[#2563eb] transition-colors font-semibold text-sm flex items-center gap-2 active:scale-[0.98] disabled:opacity-60"
          >
            {provisioning ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {provisioning ? "Provisioning…" : "Generate 6-char claim code"}
          </button>
        </div>
      )}

      {/* ── Step 2: Claim code + EA install ─────────────────────── */}
      {currentStep === 2 && prov && (
        <>
          <div className="vx-card p-6 space-y-5" data-testid="step-2-claim-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Terminal className="w-4 h-4 text-[#3b82f6]" />
                <div className="vx-label">Step 2 · Paste this code into your EA</div>
              </div>
              <div
                className="flex items-center gap-1.5 px-3 py-1 rounded-full border font-mono text-[11px]"
                style={{
                  borderColor: codeExpired ? "#ef4444" : (codeSecondsLeft < 120 ? "#f59e0b" : "#10b981"),
                  color: codeExpired ? "#ef4444" : (codeSecondsLeft < 120 ? "#f59e0b" : "#10b981"),
                }}
                data-testid="claim-code-ttl"
              >
                <Clock className="w-3 h-3" />
                {codeExpired ? "EXPIRED" : `expires in ${Math.floor(codeSecondsLeft / 60)}:${String(codeSecondsLeft % 60).padStart(2, "0")}`}
              </div>
            </div>

            <CopyBox
              label="Your 6-char claim code"
              value={prov.claim_code}
              testId="claim-code"
              big
            />

            <div className="grid md:grid-cols-2 gap-4">
              <CopyBox label="Backend URL (add to MT5 whitelist)" value={backendUrl} testId="backend-url" />
              <div className="flex items-end gap-2">
                <button
                  onClick={regenerateCode}
                  disabled={regen}
                  data-testid="regenerate-code-btn"
                  className="h-[38px] px-3 rounded-md border border-[#f59e0b]/40 text-[#f59e0b] hover:bg-[#f59e0b]/10 transition-colors flex items-center gap-1.5 text-xs font-mono disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${regen ? "animate-spin" : ""}`} />
                  Regenerate code
                </button>
                <button
                  onClick={startOver}
                  data-testid="start-over-btn"
                  className="h-[38px] px-3 rounded-md border border-[#1f2937] text-[#9ca3af] hover:bg-[#1f2937] transition-colors text-xs font-mono"
                >
                  Discard &amp; restart
                </button>
              </div>
            </div>

            {isPreview && (
              <div
                className="flex items-start gap-2 px-3 py-2 rounded-md text-[11px] font-mono"
                style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.4)", color: "#f59e0b" }}
                data-testid="preview-env-warning"
              >
                <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <div>
                  <b>Preview environment detected.</b> The downloaded EA defaults to <span className="text-[#e5e7eb]">{productionUrl}</span>.
                  For this test, override <span className="text-[#e5e7eb]">InpBackendUrl</span> in the EA inputs to <span className="text-[#e5e7eb]">{backendUrl}</span>,
                  or switch to production at <span className="text-[#e5e7eb]">{productionUrl}</span>.
                </div>
              </div>
            )}

            <button
              onClick={downloadEA}
              disabled={downloading}
              data-testid="download-ea-v151-btn"
              className="h-11 px-6 rounded-md bg-[#10b981] hover:bg-[#059669] transition-colors font-semibold text-sm text-black flex items-center gap-2 active:scale-[0.98] disabled:opacity-60"
            >
              <Download className="w-4 h-4" /> {downloading ? "Preparing…" : "Download veerox_ea_v1_51.mq5"}
            </button>

            {/* Live poll status */}
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#0a0e1a] border border-[#1f2937] text-xs font-mono"
              data-testid="polling-indicator"
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin text-[#3b82f6]" />
              <span className="text-[#9ca3af]">Waiting for EA handshake · polling every 5s ·</span>
              <span className="text-[#e5e7eb]">last check {fmtMs(status?.server_time)}</span>
            </div>
          </div>

          {/* Instructions */}
          <div className="vx-card p-6 space-y-4" data-testid="step-2-instructions">
            <div className="vx-label mb-1">Installation (5 steps · ~2 minutes)</div>
            <ol className="space-y-3 text-sm text-[#9ca3af] leading-relaxed list-decimal pl-5">
              <li>
                Click <b>Download veerox_ea_v1_51.mq5</b> above and save the file.
              </li>
              <li>
                In MT5, press <Kbd>F4</Kbd> to open MetaEditor. Copy the file into the
                <span className="font-mono text-[#e5e7eb]"> MQL5/Experts/</span> folder and press <Kbd>F7</Kbd> to compile
                (expect <b>0 errors, 0 warnings</b>).
              </li>
              <li>
                In MT5 <Kbd>Tools</Kbd> → <Kbd>Options</Kbd> → <Kbd>Expert Advisors</Kbd>, tick
                <b> Allow WebRequest for listed URL</b> and add <span className="font-mono text-[#e5e7eb]">{backendUrl}</span>.
              </li>
              <li>
                Drag <b>veerox_ea_v1_51</b> onto any chart. In the <b>Inputs</b> tab set:
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li><span className="font-mono">InpBackendUrl</span> = <span className="font-mono text-[#e5e7eb]">{backendUrl}</span></li>
                  <li><span className="font-mono">InpClaimCode</span> = <span className="font-mono text-[#e5e7eb]">{prov.claim_code}</span> <span className="text-[#6b7280]">(this run only)</span></li>
                  <li><span className="font-mono">InpApiKey</span> = <span className="font-mono text-[#6b7280]">(leave empty — the EA will print the key on first success)</span></li>
                </ul>
              </li>
              <li>
                Press <Kbd>Ctrl</Kbd>+<Kbd>E</Kbd> to enable AutoTrading. Watch the <b>Experts</b> tab —
                you should see <span className="font-mono">[Veerox] handshake OK (first)</span>. This page
                will auto-advance within 5 seconds.
              </li>
            </ol>
          </div>
        </>
      )}

      {/* ── Step 3: Live / Success ───────────────────────────────── */}
      {currentStep === 3 && status && (
        <>
          <div className="vx-card p-6" data-testid="step-3-success-card"
            style={{ borderColor: "#10b981", background: "linear-gradient(90deg, rgba(16,185,129,0.08), rgba(16,185,129,0.02))" }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#10b981]/15 border border-[#10b981] flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-[#10b981]" />
              </div>
              <div>
                <div className="font-heading text-xl font-bold text-[#10b981]">EA connected · handshake verified</div>
                <div className="text-xs font-mono text-[#9ca3af] mt-0.5">
                  last handshake {fmtMs(status.last_handshake_at)} · polling every 5s
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <KV icon={Building2}  label="Broker company" value={status.broker_company} testId="kv-broker-company" />
            <KV icon={Server}     label="Broker server"  value={status.broker_server}  testId="kv-broker-server" />
            <KV icon={Hash}       label="MT5 login"      value={status.account_login}   testId="kv-account-login" />
            <KV icon={LinkIcon}   label="Currency / Leverage"
                value={status.currency ? `${status.currency} · 1:${status.leverage || "—"}` : "—"} testId="kv-currency" />
            <KV icon={ShieldAlert} label="Account type"  value={status.is_demo ? "DEMO" : "LIVE"} testId="kv-account-type" />
            <KV icon={Terminal}   label="EA version"     value={status.ea_version}      testId="kv-ea-version" />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="vx-card p-5" data-testid="live-balance-card">
              <div className="vx-label mb-2">Balance</div>
              <div className="font-mono text-3xl font-black tracking-tight">
                {fmtCurrency(status.balance, status.currency || "USD")}
              </div>
            </div>
            <div className="vx-card p-5" data-testid="live-equity-card">
              <div className="vx-label mb-2 flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Equity</div>
              <div className="font-mono text-3xl font-black tracking-tight text-[#10b981]">
                {fmtCurrency(status.equity, status.currency || "USD")}
              </div>
            </div>
            <div className="vx-card p-5" data-testid="live-margin-card">
              <div className="vx-label mb-2">Margin used</div>
              <div className="font-mono text-3xl font-black tracking-tight">
                {fmtCurrency(status.margin_used, status.currency || "USD")}
              </div>
              <div className="text-[11px] text-[#6b7280] font-mono mt-1">last tick {fmtMs(status.last_heartbeat)}</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={finishAndOpenDashboard}
              data-testid="open-dashboard-btn"
              className="h-11 px-6 rounded-md bg-[#3b82f6] hover:bg-[#2563eb] transition-colors font-semibold text-sm flex items-center gap-2 active:scale-[0.98]"
            >
              Open dashboard <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => { setProv(null); setStatus(null); }}
              data-testid="connect-another-btn"
              className="h-11 px-5 rounded-md border border-[#1f2937] hover:bg-[#1f2937] transition-colors text-sm font-mono"
            >
              Connect another MT5 account
            </button>
          </div>
        </>
      )}
    </div>
  );
}
